import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { FormViewService, SubmissionService, ToastService } from '@xilo-mono/form-contracts';
import * as SubmissionActions from './submission.actions';
import { Router } from '@angular/router';
import { cloneDeep, unset } from 'lodash';
import { Store } from '@ngrx/store';
import { FormViewerPartialState } from '..';
import { of, switchMap, catchError, concatMap, map, tap } from 'rxjs';

@Injectable()
export class SubmissionEffects {
  loadSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmissionActions.loadSubmission),
      switchMap((action) => {
        return this.submissionService.getSubmission(action.id);
      }),
      switchMap((res) =>
        of(
          SubmissionActions.loadSubmissionSuccess({
            submission: res,
            note: res.note,
          })
        )
      ),
      catchError((e) =>
        of(SubmissionActions.loadSubmissionFailure({ error: e }))
      )
    )
  );

  submissionLoadError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmissionActions.loadSubmissionFailure),
      map((error) => {
        const metadata = this.submissionService.metadata;
        return SubmissionActions.createSubmission({ metadata });
      })
    )
  );

  createSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmissionActions.createSubmission),
      concatMap(({ responses, metadata }) => {
        const meta = cloneDeep(metadata);
        unset(meta, 'clientId');
        unset(meta, 'submissionId');
        return this.submissionService
          .newCustomerSubmission(meta, responses)
          .pipe(
            map((submission) => {
              this.formViewService.submission = cloneDeep(submission);
              return SubmissionActions.createSubmissionSuccess({
                submission,
              });
            }),
            catchError((error) => {
              return of(SubmissionActions.createSubmissionFailure({ error }));
            })
          );
      })
    )
  );

  updateSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmissionActions.updateSubmission),
      switchMap((action) =>
        this.submissionService.updateSubmission(action.submission).pipe(
          map((res) => {
            return SubmissionActions.updateSubmissionSuccess({
              submission: res,
            });
          }),
          catchError((e) =>
            of(SubmissionActions.updateSubmissionFailure({ error: e }))
          )
        )
      )
    )
  );

  submitFormSubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SubmissionActions.submitForm),
      concatMap(({ clientId, formId, companyId, submissionId, agentId, formType }) => {
        return this.submissionService
          .submitForm(clientId, formId, companyId, submissionId, agentId, formType)
          .pipe(
            map((message) => {
              const token = localStorage.getItem('token');
              return SubmissionActions.submitFormSuccess({
                token,
                clientId,
              });
            }),
            catchError((error) => {
              if (error?.error?.error?.message === 'Submission requires an agent assignment') {
                this.toastService.showWarn('Please assign an agent')
              }
              return of(SubmissionActions.submitFormFailure({ error }));
            })
          );
      })
    )
  );

  submitFormFailure$ = createEffect(() =>  {
    return this.actions$.pipe(
      ofType(SubmissionActions.submitFormFailure),
      tap((error) => console.log('Failed to submit form: ', error))
    )
  },
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private formViewService: FormViewService,
    private router: Router,
    private submissionService: SubmissionService,
    private toastService: ToastService,
    private store: Store<FormViewerPartialState>
  ) {}
}
